import React, { useState } from 'react';
import {
  Box,
  Button,
  ButtonGroup,
  Flex,
  Input,
  Text,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { MdDownload, MdContentCopy } from 'react-icons/md';
import { observer } from 'mobx-react';
import QRCodeStyling from 'qr-code-styling';
import QrPreview from './QrPreview';
import { generateFilename, downloadBlob, copyToClipboard, copyTextToClipboard } from '../utils/exportUtils';
import { sendEvent } from '../../../common/analytics';

const SIZES = [256, 512, 1024];

const QrPreviewPanel = observer(({ store }) => {
  const toast = useToast();
  const border = useColorModeValue('gray.200', 'gray.700');
  const previewBg = useColorModeValue('gray.50', 'gray.900');
  const secondaryText = useColorModeValue('gray.500', 'gray.400');

  // Local state for the custom size input so typing isn't clamped mid-keystroke
  const [customInput, setCustomInput] = useState(String(store.customExportSize));

  const getExportSize = () =>
    store.exportSize === 'custom' ? store.customExportSize : Number(store.exportSize);

  // Always create a fresh QRCodeStyling instance at the exact export size
  const buildQr = (w, h) => {
    const opts = {
      ...store.getQrStylingOptions(),
      data: store.generatedPayload || 'https://vorakit.app',
      width: w,
      height: h,
    };
    return new QRCodeStyling(opts);
  };

  const hasPayload = !!store.generatedPayload;

  const dl = async (format) => {
    try {
      const s = getExportSize();
      const qr = buildQr(s, s);
      const blob = await qr.getRawData(format);
      if (!blob) throw new Error();
      downloadBlob(blob, generateFilename(store.qrTypeId, format));
      store.addToRecent(store.payload, store.qrTypeId);
      toast({ title: 'Downloaded!', status: 'success', duration: 1800, position: 'bottom' });
      sendEvent('QRCode', 'Download', format.toUpperCase());
    } catch {
      toast({ title: 'Download failed', status: 'error', duration: 2500, position: 'bottom' });
    }
  };

  const copyPng = async () => {
    try {
      const qr = buildQr(512, 512);
      const blob = await qr.getRawData('png');
      if (blob && await copyToClipboard(blob)) {
        toast({ title: 'Copied!', status: 'success', duration: 1800, position: 'bottom' });
        sendEvent('QRCode', 'Copy', 'PNG');
      } else throw new Error();
    } catch {
      toast({ title: 'Copy failed', status: 'error', duration: 2500, position: 'bottom' });
    }
  };

  const copyPayload = async () => {
    const p = store.payload;
    if (!p) { toast({ title: 'Nothing to copy', status: 'warning', duration: 1800, position: 'bottom' }); return; }
    if (await copyTextToClipboard(p)) {
      toast({ title: 'Copied!', status: 'success', duration: 1800, position: 'bottom' });
      sendEvent('QRCode', 'Copy', 'Payload');
    }
  };

  return (
    <Box overflow="hidden" h="100%">
      {/* Preview area */}
      <Box
        bg={previewBg}
        p={{ base: 6, md: 10 }}
        display="flex"
        alignItems="center"
        justifyContent="center"
        minH="320px"
      >
        <QrPreview store={store} />
      </Box>

      {/* Action bar */}
      <Box px={5} py={4} borderTopWidth="1px" borderColor={border}>
        {/* Size selector */}
        <Flex align="center" gap={3} mb={4} flexWrap="wrap">
          <Text fontSize="xs" color={secondaryText} flexShrink={0}>
            Size
          </Text>
          <ButtonGroup size="xs" isAttached>
            {SIZES.map((s) => (
              <Button
                key={s}
                variant={store.exportSize === s ? 'solid' : 'outline'}
                colorScheme={store.exportSize === s ? 'blue' : 'gray'}
                onClick={() => store.setExportSize(s)}
              >
                {s}
              </Button>
            ))}
            <Button
              variant={store.exportSize === 'custom' ? 'solid' : 'outline'}
              colorScheme={store.exportSize === 'custom' ? 'blue' : 'gray'}
              onClick={() => store.setExportSize('custom')}
            >
              Custom
            </Button>
          </ButtonGroup>
          {store.exportSize === 'custom' && (
            <Input
              type="number"
              size="xs"
              w="90px"
              value={customInput}
              onChange={(e) => setCustomInput(e.target.value)}
              onBlur={() => {
                const n = parseInt(customInput, 10);
                const clamped = isNaN(n) ? 512 : Math.max(1, Math.min(4096, n));
                store.setCustomExportSize(clamped);
                setCustomInput(String(clamped));
              }}
              min={1}
              max={4096}
              placeholder="512"
            />
          )}
        </Flex>

        {/* Download / copy actions — single row */}
        <Flex gap={2} align="center">
          <Button
            colorScheme="blue"
            size="sm"
            onClick={() => dl('png')}
            isDisabled={!hasPayload}
            leftIcon={<MdDownload size={15} />}
            flex="1"
          >
            PNG
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => dl('svg')}
            isDisabled={!hasPayload}
            leftIcon={<MdDownload size={15} />}
            flex="1"
          >
            SVG
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={copyPng}
            isDisabled={!hasPayload}
            leftIcon={<MdContentCopy size={14} />}
            flex="1"
          >
            PNG
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={copyPayload}
            isDisabled={!hasPayload}
            leftIcon={<MdContentCopy size={14} />}
            flex="1"
          >
            Text
          </Button>
        </Flex>
      </Box>
    </Box>
  );
});

export default QrPreviewPanel;
